import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { DownloadIcon, XMarkIcon } from '@/resources';

type ModalProps = {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onDownloadClick?: () => void;
  modalClasses?: string;
  showDownloadIcon?: boolean;
  descriptionClasses?: string;
  height?: string;
  label?: string;
  labelClasses?: string;
  description?: string;
  subtext?: string;
  subtextClasses?: string;
  centered?: boolean;
  hasButton?: boolean;
  hasCancelButton?: boolean;
  buttonClasses?: string;
  buttonType?: ButtonVariant;
  buttonText?: string;
  buttonDataTestId?: string;
  handleSubmit?: () => void;
  isButtonDisabled?: boolean;
  downloadAssetUrl?: string;
  downloadAssetName?: string;
  containerClasses?: string;
  isModalWithSearchAndFilters?: boolean;
  hasCloseIcon?: boolean;
  disableOverlayClick?: boolean;
};

export const Modal = ({
  children,
  isOpen,
  modalClasses,
  descriptionClasses,
  height,
  label,
  labelClasses,
  onClose,
  description,
  subtext,
  subtextClasses,
  centered,
  hasButton,
  hasCancelButton,
  buttonClasses,
  buttonType,
  buttonText,
  buttonDataTestId,
  handleSubmit,
  isButtonDisabled,
  showDownloadIcon,
  downloadAssetUrl,
  downloadAssetName,
  containerClasses = '',
  isModalWithSearchAndFilters = false,
  hasCloseIcon = true,
  disableOverlayClick = false
}: ModalProps) => {
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      if (!disableOverlayClick) {
        onClose();
      }
    }
  };

  const [t] = useTranslation('common');

  return (
    <div
      className={`${
        isOpen
          ? 'modal-backdrop-color fixed z-50 flex flex-col items-center justify-center backdrop-blur-[1px]'
          : 'hidden'
      } inset-0 size-full max-h-full p-4`}
      onClick={handleOverlayClick}>
      <div
        className={`relative ${
          modalClasses ?? 'max-h-32 max-w-[25rem]'
        } filter-pop-up-shadow overflow-y-hidden rounded-2xl bg-white`}
        onClick={(e) => e.stopPropagation()}>
        <div className={`${height ?? 'h-52'} mx-5 flex flex-col`}>
          <div className={`flex ${label ? 'justify-between' : 'justify-end'} pb-4 pr-1 pt-5`}>
            {label && (
              <div className={labelClasses ?? 'text-lg font-bold text-primary-blue'}>
                {t(`${label}`)}
              </div>
            )}
            <div className='flex items-center'>
              {showDownloadIcon && (
                <a href={downloadAssetUrl} download={downloadAssetName}>
                  <DownloadIcon className='mx-3 cursor-pointer' />
                </a>
              )}
              {hasCloseIcon && <XMarkIcon className='cursor-pointer' onClick={onClose} />}
            </div>
          </div>
          {description && (
            <div className={`flex ${centered && 'justify-center'}`}>
              <div
                className={`mb-6 text-base font-normal text-secondary-gray ${descriptionClasses}`}>
                {t(`${description}`)}
              </div>
            </div>
          )}
          {subtext && (
            <div className={`flex ${centered && 'justify-center'}`}>
              <div className={`text-base font-normal text-secondary-gray ${subtextClasses}`}>
                {t(`${subtext}`)}
              </div>
            </div>
          )}
          <div
            className={`${isModalWithSearchAndFilters ? 'flex h-[calc(100%-200px)] flex-col' : 'custom-scrollbar flex-1 overflow-y-auto'} ${containerClasses} ${!hasButton && 'mb-5'}`}>
            {children}
          </div>
          {hasButton && (
            <div className={`my-6 mr-3 ${buttonClasses}`}>
              {hasCancelButton && (
                <Button
                  type='submit'
                  variant={ButtonVariant.QUATERNARY}
                  handleOnClick={onClose}
                  className='mr-6 px-3 !text-base'>
                  {t('cancel')}
                </Button>
              )}
              <Button
                dataTestId={buttonDataTestId}
                type='submit'
                variant={buttonType ?? ButtonVariant.PRIMARY}
                handleOnClick={handleSubmit}
                disabled={isButtonDisabled}
                className={`px-3 !text-base ${isButtonDisabled && 'border-secondary-steel-blue'}`}>
                {buttonText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
